import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { UseCallDevicesResult } from "../hooks/useCallDevices";

interface CallDeviceNoticeProps {
  devices: Pick<UseCallDevicesResult, "notice" | "undo">;
}

/**
 * "Now using <device> · Undo" after a headset auto-switch, or "Switched to
 * <device> — <old> disconnected" after a fallback (GH-1859). Portaled to the
 * body so it shows on any route, whether the call is full-screen or minimised.
 */
export function CallDeviceNotice({ devices }: CallDeviceNoticeProps) {
  const { t } = useTranslation();
  if (!devices.notice) return null;
  const { notice, canUndo } = devices.notice;

  const text =
    notice.kind === "auto"
      ? t("call.controls.deviceNoticeNowUsing", { device: notice.deviceName })
      : notice.oldName
        ? t("call.controls.deviceNoticeFallback", { device: notice.deviceName, old: notice.oldName })
        : t("call.controls.deviceNoticeSwitched", { device: notice.deviceName });

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      data-call-device-notice={notice.kind}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 max-w-[calc(100vw-2rem)] ps-4 pe-2 py-2 rounded-full bg-foreground text-background text-sm font-medium shadow-xl"
    >
      <span className="truncate">{text}</span>
      {canUndo && (
        <button
          type="button"
          onClick={devices.undo}
          className="shrink-0 px-3 h-7 rounded-full bg-background/15 font-bold hover:bg-background/25 transition-colors"
        >
          {t("call.controls.deviceNoticeUndo")}
        </button>
      )}
    </div>,
    document.body,
  );
}
